<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { useGetParticipantDetails, useUpdateCampaignParticipant } from '@/api/campaignParticipant.api'
import CloseIcon from '@/assets/icons/remove.svg?component'
import DetailsActivities from '@/components/CampaignParticipants/CampaignParticipantDetails/components/DetailsActivities.vue'
import DetailsHeader from '@/components/CampaignParticipants/CampaignParticipantDetails/components/DetailsHeader.vue'
import FixedFeeForm from '@/components/CampaignParticipants/CampaignParticipantDetails/components/FixedFeeForm.vue'
import TodoList from '@/components/CampaignParticipants/CampaignParticipantDetails/components/TodoList.vue'
import CampaignParticipantsMoveToDoneConfirm from '@/components/CampaignParticipants/CampaignParticipantsMoveToDoneConfirm.vue'
import CampaignParticipantsRejectConfirm from '@/components/CampaignParticipants/CampaignParticipantsRejectConfirm.vue'
import { useCampaignDefaultPayment } from '@/lib/hooks/campaign-payment.hooks'
import { useShouldDisableMoveToDoneWarningPopup } from '@/lib/hooks/user-preferences.hooks'
import { useAdvertiserStore } from '@/stores/advertiser.stores'
import type { CampaignType } from '@/types'
import { ParticipantStatus } from '@/types/campaignParticipant.types'
import type { ParticipantsColumnType } from '@/types/campaignParticipants.types'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'

const props = defineProps<{
  isOpen: boolean
  hasFixedFee?: boolean
  campaign: CampaignType
  columnsList: ParticipantStatus[]
  columnsData: Record<ParticipantStatus, ParticipantsColumnType>
}>()
const emits = defineEmits(['moveParticipantToColumn', 'updateColumnItem'])

const store = useStore()
const router = useRouter()
const route = useRoute()

const hideStatisticsBtn = computed(
  // TODO: legacy way of getting user permissions through store
  () => store.getters['shop/hideSales'] || store.getters['shop/hideUsers'] || store.getters['shop/hideCosts'],
)
const participantId = computed(() => route.query.participant as string)
const {
  data: participant,
  getParticipantDetails,
  getActivities,
} = useGetParticipantDetails(
  props.campaign.id,
  () => emits('updateColumnItem', participant.value, 'clearNotification'),
  participantId.value,
)
const { updateParticipantStatus, isLoading: isStatusUpdateLoading } = useUpdateCampaignParticipant(props.campaign.id)
const { shouldDisableMoveToDoneWarningPopup } = useShouldDisableMoveToDoneWarningPopup()

const isConfirmRejectDialogOpen = ref(false)
const isConfirmDoneDialogOpen = ref(false)

const advertiserStore = useAdvertiserStore()
const currency = computed(() => advertiserStore?.advertiser?.currencyObj)
const defaultCampaignFixedFee = useCampaignDefaultPayment(() => props.campaign)

const onClose = async () => {
  const currentQuery = { ...route.query }
  delete currentQuery.participant
  await router.replace({ query: currentQuery })
}

const checkIfCanMoveParticipant = async (newStatus: ParticipantStatus) => {
  if (newStatus === ParticipantStatus.StoreDenied) {
    isConfirmRejectDialogOpen.value = true
  } else if (newStatus === ParticipantStatus.Done && !shouldDisableMoveToDoneWarningPopup.value) {
    isConfirmDoneDialogOpen.value = true
  } else {
    await moveParticipant(newStatus)
  }
}

const moveParticipant = async (newStatus: ParticipantStatus) => {
  const participantId = participant.value?.id
  if (participantId) {
    emits('moveParticipantToColumn', newStatus, participant)
    await updateParticipantStatus(participantId, newStatus)
    await getParticipantDetails(participantId)
  }
}

const onAdvertiserReject = async () => {
  await moveParticipant(ParticipantStatus.StoreDenied)
  emits('updateColumnItem', participant.value, 'addComment')
  isConfirmRejectDialogOpen.value = false
}

const onCancelStrategy: Record<ParticipantStatus.StoreDenied | ParticipantStatus.Done, () => void> = {
  [ParticipantStatus.StoreDenied]: () => {
    isConfirmRejectDialogOpen.value = false
  },
  [ParticipantStatus.Done]: () => {
    isConfirmDoneDialogOpen.value = false
  },
}

const onConfirmStrategy: Record<ParticipantStatus.StoreDenied | ParticipantStatus.Done, () => Promise<void>> = {
  [ParticipantStatus.StoreDenied]: onAdvertiserReject,
  [ParticipantStatus.Done]: async () => {
    await moveParticipant(ParticipantStatus.Done)
    isConfirmDoneDialogOpen.value = false
  },
}

const isMoveToDoneDialogVisible = computed(() => !shouldDisableMoveToDoneWarningPopup.value && isConfirmDoneDialogOpen.value)
</script>
<template>
  <el-drawer :model-value="isOpen" :with-header="false" :destroy-on-close="true" size="min(100%, 640px)" :before-close="onClose">
    <div v-if="!participant">
      <BoxComponent :mt="42" :mb="42">
        <FlexComponent justify="space-around">
          <el-icon :size="42" color="black">
            <loading-spinner :size="42" />
          </el-icon>
        </FlexComponent>
      </BoxComponent>
    </div>
    <template v-else>
      <div class="participant-details__close" @click="onClose">
        <el-icon :size="24">
          <CloseIcon />
        </el-icon>
      </div>
      <BoxComponent>
        <BoxComponent v-if="participant" :mb="32">
          <DetailsHeader
            :participant="participant"
            :column-ids="columnsList"
            :hide-statistics-btn="hideStatisticsBtn"
            @move="(status) => checkIfCanMoveParticipant(status)"
          />
        </BoxComponent>
        <BoxComponent v-if="participant.todo && participant.todo?.length" :mb="24">
          <TodoList
            :campaign-id="campaign.id"
            :participant-id="participant.id"
            :todo="participant.todo"
            :on-change="
              async (items) => {
                emits('updateColumnItem', participant, 'todo', { todo: items })
                await getActivities(participant.id)
              }
            "
          />
        </BoxComponent>
        <BoxComponent v-if="(hasFixedFee || participant.payment_amount_v2) && currency" :mb="24">
          <FixedFeeForm
            :campaign-id="campaign.id"
            :participant-id="participant.id"
            :participant-fixed-fee="participant.payment_amount_v2?.amount ? Number(participant.payment_amount_v2.amount) : null"
            :default-campaign-fixed-fee="defaultCampaignFixedFee"
            :show-reset-to-default-button="participant.status !== ParticipantStatus.Done && campaign.hasAdditionalPayment"
            :is-disabled="participant.status === ParticipantStatus.Done"
            :currency="currency"
            @on-change="
              async (participantFixedFee: number) => {
                const fixedFee = participantFixedFee === defaultCampaignFixedFee ? null : participantFixedFee
                emits('updateColumnItem', participant, 'changeFixedFee', { fixedFee, currency })
                await getActivities(participant.id)
              }
            "
          />
        </BoxComponent>
        <BoxComponent>
          <DetailsActivities
            :participant="participant"
            @on-delete="
              async () => {
                emits('updateColumnItem', participant, 'deleteComment')
                await getActivities(participant.id)
              }
            "
            @on-comment-add="
              async () => {
                emits('updateColumnItem', participant, 'addComment')
                await getActivities(participant.id)
              }
            "
          />
        </BoxComponent>
      </BoxComponent>
      <CampaignParticipantsRejectConfirm
        v-model="isConfirmRejectDialogOpen"
        :participant="participant"
        :campaign-id="campaign.id"
        @on-confirm="onConfirmStrategy[ParticipantStatus.StoreDenied]"
        @on-cancel="onCancelStrategy[ParticipantStatus.StoreDenied]"
      />
      <CampaignParticipantsMoveToDoneConfirm
        v-model="isMoveToDoneDialogVisible"
        :is-loading="isStatusUpdateLoading"
        :on-submit="(value: boolean) => (shouldDisableMoveToDoneWarningPopup = value)"
        @on-cancel="onCancelStrategy[ParticipantStatus.Done]"
        @on-confirm="onConfirmStrategy[ParticipantStatus.Done]"
      />
    </template>
  </el-drawer>
</template>
<style lang="scss" scoped>
:deep(input[type='text']) {
  height: initial;
}
:deep(.headline-medium),
:deep(.headline-small) {
  margin-bottom: 0;
}
:deep(.el-checkbox.el-checkbox--large) {
  height: 24px;

  .el-checkbox__inner {
    border-radius: 4px;
    width: 18px;
    height: 18px;

    &:after {
      height: 9px;
      top: 6px;
    }
  }
}
:deep(.el-button--info) {
  border-color: #fff;
  &:hover,
  &:focus,
  &:active {
    border-color: #fff;
  }
}
:deep(.el-select) {
  .el-input__inner {
    text-transform: capitalize;
  }

  .el-select-dropdown__item span {
    text-transform: capitalize;
  }
}

.participant-details__close {
  cursor: pointer;
  position: absolute;
  width: 48px;
  height: 48px;
  top: 12px;
  right: 12px;
  padding: 12px;
  z-index: 1;
}
</style>
<style>
.participant-details-content__popover {
  &.el-popover {
    text-align: left;
  }

  &.el-popover.el-popper {
    border-radius: 16px;
    padding: 12px 0;

    /* Card shadow */
    box-shadow: 4px 4px 32px 0 rgba(0, 0, 0, 0.08);
  }
}
.participant-details-content__icon-popover {
  &.el-popover {
    text-align: left;
  }

  &.el-popover.el-popper {
    border-radius: 16px;
    padding: 16px 0;

    /* Card shadow */
    box-shadow: 0px 2px 8px 0px rgba(60, 75, 93, 0.16);
  }
}
</style>
